import { useId } from "react";

/* Labelled form controls.
 *
 * A placeholder is not a label: it disappears the moment the user types, isn't
 * reliably announced by screen readers, and leaves the field nameless. Every
 * control here gets a real <label for>. Set `hideLabel` to keep the existing
 * visual density — it stays in the accessibility tree either way. */

function useFieldId(explicitId) {
  const generated = useId();
  return explicitId || generated;
}

export function Field({ label, hideLabel = false, hint, id, children }) {
  const fieldId = useFieldId(id);
  const hintId = hint ? `${fieldId}-hint` : undefined;

  return (
    <div className="field">
      <label className={hideLabel ? "field__label field__label--visually-hidden" : "field__label"} htmlFor={fieldId}>
        {label}
      </label>
      {children({ id: fieldId, "aria-describedby": hintId })}
      {hint && <p className="field__hint" id={hintId}>{hint}</p>}
    </div>
  );
}

export function TextField({ label, hideLabel, hint, value, onChange, type = "text", ...rest }) {
  return (
    <Field label={label} hideLabel={hideLabel} hint={hint}>
      {(fieldProps) => (
        <input
          {...fieldProps}
          {...rest}
          type={type}
          value={value}
          onChange={(event) => onChange(event.target.value)}
        />
      )}
    </Field>
  );
}

export function TextAreaField({ label, hideLabel, hint, value, onChange, ...rest }) {
  return (
    <Field label={label} hideLabel={hideLabel} hint={hint}>
      {(fieldProps) => (
        <textarea
          {...fieldProps}
          {...rest}
          value={value}
          onChange={(event) => onChange(event.target.value)}
        />
      )}
    </Field>
  );
}

export function SelectField({ label, hideLabel, hint, value, onChange, options, placeholder, ...rest }) {
  return (
    <Field label={label} hideLabel={hideLabel} hint={hint}>
      {(fieldProps) => (
        <select
          {...fieldProps}
          {...rest}
          value={value}
          onChange={(event) => onChange(event.target.value)}
        >
          {placeholder && <option value="">{placeholder}</option>}
          {options.map((option) => (
            <option key={option} value={option}>{option}</option>
          ))}
        </select>
      )}
    </Field>
  );
}

export function FileField({ label, hideLabel, hint, onChange, inputRef, ...rest }) {
  return (
    <Field label={label} hideLabel={hideLabel} hint={hint}>
      {(fieldProps) => (
        <input
          {...fieldProps}
          {...rest}
          ref={inputRef}
          type="file"
          accept="image/*"
          onChange={(event) => onChange(event.target.files[0] || null)}
        />
      )}
    </Field>
  );
}
